/**
 * The meal a donor already chose, shown back to them on the headcount step.
 *
 * Read-only on purpose: changing the meal is what Back is for, and a card
 * that looked tappable here would imply a second way to do it. The same
 * picture, name and price as the menu, so the donor recognises it instantly.
 */

import { memo } from 'react';
import { Image, StyleSheet, View } from 'react-native';

import { formatMoney, type Meal } from '@mfa/domain';
import { mealImageSource } from '../../assets';
import { colors, radius, sizing, space } from '../../theme';
import { Text } from '../common/Text';
import { useT } from '../../core/i18n/LanguageProvider';
import { useLocalized } from '../../core/i18n/content';

export interface SelectedMealCardProps {
  meal: Meal;
  testID?: string;
}

export const SelectedMealCard = memo(function SelectedMealCard({
  meal,
  testID = 'selected-meal',
}: SelectedMealCardProps) {
  const t = useT();
  const pick = useLocalized();
  const source = mealImageSource(meal.imageUrl, meal.name);

  return (
    <View
      style={styles.card}
      testID={testID}
      accessibilityRole="summary"
      accessibilityLabel={`${t('You selected')}: ${pick(meal.name, meal.nameUr)}, ${formatMoney(meal.unitPrice)} ${t('per person')}`}
    >
      <View style={styles.thumbWrap}>
        {source ? (
          <Image source={source} style={styles.thumb} accessibilityIgnoresInvertColors />
        ) : (
          <View style={[styles.thumb, styles.thumbFallback]} />
        )}
      </View>

      <View style={styles.body}>
        <Text variant="caption" tone="tertiary">{t('You selected')}</Text>
        <Text variant="cardTitle" numberOfLines={2}>
          {pick(meal.name, meal.nameUr)}
        </Text>
        <Text variant="caption" tone="secondary" numberOfLines={2}>
          {pick(meal.description, meal.descriptionUr)}
        </Text>
        <View style={styles.priceRow}>
          <Text variant="amountSmall" tone="brand">
            {formatMoney(meal.unitPrice)}
          </Text>
          <Text variant="caption" tone="secondary">{t('Per Person')}</Text>
        </View>
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.md,
    padding: space.sm,
    borderRadius: radius.lg,
    backgroundColor: colors.bg.surface,
    borderWidth: 1,
    borderColor: colors.border.default,
  },
  thumbWrap: {
    borderRadius: radius.md,
    overflow: 'hidden',
  },
  thumb: {
    width: sizing.mealRowThumb,
    height: sizing.mealRowThumb,
    resizeMode: 'cover',
  },
  thumbFallback: {
    backgroundColor: colors.bg.surfaceSunken,
  },
  body: {
    flex: 1,
    gap: space.xxs,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: space.xs,
  },
});
